import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useUpdateTask } from "../../hooks/useTaskApi";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/AuthContext";

const epochToDate = (epoch) =>
  epoch ? new Date(epoch * 1000).toISOString().split("T")[0] : "";

const dateToEpoch = (dateStr) => Math.floor(new Date(dateStr).getTime() / 1000);

export const EditTaskModal = ({ task, onClose }) => {
  const [employees, setEmployees] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await api.get("/employees");
        setEmployees(res.data.data.employees);
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };
    fetchEmployees();
  }, []);

  const { register, handleSubmit, reset } = useForm();
  const { mutate: updateTask, isLoading } = useUpdateTask();

  useEffect(() => {
    if (task) {
      reset({
        title: task?.title || "",
        description: task?.description || "",
        priority: task?.priority || "low",
        status: task?.status || "",
        startDate: epochToDate(task.startDate),
        endDate: epochToDate(task.endDate),
        assignedTo: task?.assignedTo?._id || "",
        assignedBy: task?.assignedBy?._id || "",
        project_id: task?.project_id || ""
      });
    }
  }, [task, reset]);

  const onSubmit = (formData) => {
    const updatedData = {
      ...formData,
      startDate: dateToEpoch(formData.startDate),
      endDate: dateToEpoch(formData.endDate),
      updatedBy: user?.employeeProfile?._id,
      project_id: formData.project_id,
    };

    updateTask(
      { taskId: task._id, data: updatedData, createdBy: user._Id },
      {
        onSuccess: () => {
          toast.success('Task updated successfully!');
          onClose();
        },
        onError: (error) => {
          alert(`Error: ${error.response?.data?.message || error.message}`);
        },
      }
    );
  };

  if (!task) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex justify-center items-center">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg h-auto p-5 transition-all scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {/* Header */}
        <div className="flex justify-between items-center pb-3 mb-3 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-800">Edit Task</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {/* ✅ Hidden input to store project_id - MUST be registered */}
          <input type="hidden" {...register("project_id")} />

          <Input id="title" label="Task Title" register={register} required />

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="text-gray-700 mb-1 block text-xs font-medium"
            >
              Task Description
            </label>
            <textarea
              id="description"
              {...register("description")}
              rows={2}
              className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter task description"
              required
            />
          </div>

          {/* Select Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-700 mb-1 block text-xs font-medium">
                Priority
              </label>
              <select
                {...register("priority")}
                className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="text-gray-700 mb-1 block text-xs font-medium">
                Status
              </label>
              <select
                {...register("status")}
                className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="to-do">To Do</option>
                <option value="pending">Pending</option>
                <option value="hold">Hold</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Assigned To */}
            <div>
              <label className="text-gray-700 mb-1 block text-xs font-medium">
                Assigned To
              </label>

              {user?.role === "employee" ? (
                <p className="w-full p-2 text-sm border rounded-lg bg-gray-50 text-gray-700">
                  {task.assignedTo?.name || "N/A"}
                </p>
              ) : (
                <select
                  {...register("assignedTo")}
                  className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  defaultValue={task.assignedTo?._id}
                  required
                >
                  {employees.map((emp) => (
                    <option key={emp?._id} value={emp?._id}>
                      {emp?.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Assigned By */}
            <div>
              <label className="text-gray-700 mb-1 block text-xs font-medium">
                Assigned By
              </label>
              <select
                {...register("assignedBy")}
                className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                defaultValue={task.assignedBy?._id}
                required
              >
                {employees.map((emp) => (
                  <option key={emp?._id} value={emp?._id}>
                    {emp?.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="startDate"
                className="text-gray-700 mb-1 block text-xs font-medium"
              >
                Start Date
              </label>
              <input
                type="date"
                {...register("startDate")}
                className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label
                htmlFor="endDate"
                className="text-gray-700 mb-1 block text-xs font-medium"
              >
                End Date
              </label>
              <input
                type="date"
                {...register("endDate")}
                className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-3 mt-1 sticky bottom-0 bg-white border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="px-3 py-1.5 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="px-3 py-1.5 text-sm"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
